import dayjs from 'dayjs';
import { useMemo } from 'react';

import useSubscriptionPlan from '@/hooks/useSubscriptionPlan';
import { isAnnualSpecialPlan, OfferingType, PLAN, PLAN_NAME } from '@/types/plan';
import { ORIGIN_PRIORITY } from '@/utils/pay/promotional-payment-action';

import { useUserAutoReply } from './useAutoReply';
import { useUser } from './useUser';

// 1206 기능변경
// subscription 의 origin_plan 을 따라가도록 변경 (기능위주)

// 요금제별 기능 분류를 위한 훅
// 요금제가 없는 경우 Free로 간주
// Freetrial은 Standard로 간주
// Franchise는 Standard로 간주
export const useUserPlan = () => {
  const { data: user, isLoading } = useUser();

  const userPlan = useMemo(() => {
    const originPlan = user?.subscription?.plan?.origin_plan;

    switch (originPlan) {
      case PLAN.STANDARD:
        return PLAN_NAME.Standard;
      case PLAN.EFFICIENT:
        return PLAN_NAME.Efficient;
      case PLAN.SPECIAL_PLUS:
        return PLAN_NAME.SpecialPlus;
      case PLAN.SPECIAL:
        return PLAN_NAME.Special;
      default:
        // Default case
        return PLAN_NAME.Free;
    }
  }, [user?.subscription?.plan?.origin_plan]);

  return {
    data: userPlan,
    offeringType: user?.subscription?.plan?.offering_type,
    originPlanName: user?.subscription?.plan?.name,
    isLoading,
  };
};

export const useIsFreeTrial = () => {
  const { data: user, ...args } = useUser();
  const isFreeTrial = user?.subscription?.plan?.name === PLAN_NAME.FreeTrial;

  return {
    isFreeTrial: !!isFreeTrial,
    ...args,
  };
};

/**
 * PROMOTIONAL(또는 연간 스페셜 구독중) → 같은 tier 전환 시 RESERVE 흐름 여부.
 * 첫달무료(SPECIAL)→스페셜, 두달무료(SPECIAL)→스페셜처럼 origin_plan이 같으면
 * 즉시 결제가 아니라 프로모션 종료일(`subscription.end_date`)에 결제를 예약함.
 *
 * 연간 스페셜 "구독중"(RETAIL·special·period>=12)도 같은 tier(월간 전환)/다운그레이드는
 * 즉시가 아니라 연간 종료일부터 적용(APPLY_NEXT)이라 reserve 흐름으로 취급한다.
 * 이게 없으면 special-form의 "관리 시작일"이 연간 구독자에게 "내일"로 거짓 표시된다.
 * (월간 RETAIL은 여전히 false — 기존 동작 무변경)
 *
 * 단, 종료일이 이미 지났으면(만료) 예약할 미래 시점이 없으므로 RESERVE가 아니라 즉시 결제(SUBSCRIBE)다.
 * /user/me 는 종료 후 4h grace로 구독을 계속 내려주지만, RESERVE 분기는 BE checkout-intent의
 * `end_date > now` 와 동일한 strict 기준을 따라야 400('예약 결제는 활성 구독이 있을 때만 가능합니다.')을 피한다.
 */
export const useIsReserveFlow = (targetOriginPlan: PLAN | undefined) => {
  const { data: user } = useUser();

  return useMemo(() => {
    if (!user?.subscription?.plan || !targetOriginPlan) return false;
    const isPromotional = user.subscription.plan.offering_type === OfferingType.PROMOTIONAL;
    const isAnnualSpecial = isAnnualSpecialPlan(user.subscription.plan);
    const endDate = user.subscription.end_date;
    const isNotExpired = !!endDate && dayjs(endDate).isAfter(dayjs());
    // getPromotionalPaymentDecision과 동일 기준: 같은 tier 또는 다운그레이드 → 예약(무료 유지).
    // 상위 업그레이드만 즉시결제라 reserve 아님
    const currentPriority = ORIGIN_PRIORITY[user.subscription.plan.origin_plan ?? ''] ?? 0;
    const targetPriority = ORIGIN_PRIORITY[targetOriginPlan] ?? 0;
    const isUpgrade = targetPriority > currentPriority;
    return (isPromotional || isAnnualSpecial) && isNotExpired && !isUpgrade;
  }, [user, targetOriginPlan]);
};

interface UseIsUserPlanSpecialOptions {
  /** 위즈더플래닝(ENTERPRISE)은 스페셜 요금제이지만 블라인드 등 일부 기능에서 제외 */
  excludeEnterprise?: boolean;
}

export const useIsUserPlanSpecial = (options?: UseIsUserPlanSpecialOptions) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data, offeringType, ...args } = useUserPlan();
  const { data: isAutoReply } = useUserAutoReply();
  const { originPlan } = useSubscriptionPlan();

  const specials = [PLAN.SPECIAL, PLAN.SPECIAL_PLUS];

  // 위즈더플래닝(ENTERPRISE)은 스페셜 요금제이지만 블라인드 등 일부 기능에서 제외
  const isSpecialPlan =
    specials.includes(originPlan) &&
    !(options?.excludeEnterprise && offeringType === OfferingType.ENTERPRISE);

  // isSpecialAndAutoReply - UI상 SpecialPlus는 항상 true 가 되어야함
  return {
    isSpecial: isSpecialPlan,
    isAutoReply: !!isAutoReply?.is_auto_reply,
    isSpecialAndAutoReply:
      originPlan === PLAN.SPECIAL_PLUS ||
      (originPlan === PLAN.SPECIAL && !!isAutoReply?.is_auto_reply),
    ...args,
  };
};

export const aboveStandardPlans = [PLAN_NAME.Standard, PLAN_NAME.Special, PLAN_NAME.SpecialPlus];
